import { EntityBulkGridDialog } from '@/common/entityFramework/bulk/EntityBulkGridDialog';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { useEffect, useMemo, useState } from 'react';
import { ProgContextStrip } from '../prog/ProgContextStrip';

const PCT_CALC_TYPES = ['PCT_OFF', 'MAX_ALLOWED_PCT'];

// Column headers mirror the PROG_PROD sheet of the Contract Excel upload so the same spreadsheet works in both channels
export const ProgProdBulkDialog = ({ closeDialog, progObj, existingRows = [] }) => {
  const rsrc = useRsrc();
  const [fieldDefs, setFieldDefs] = useState(null);

  useEffect(() => {
    rsrc.fieldDefs('md', 'PROG_PROD').then((defs) => setFieldDefs(Object.fromEntries(defs.map((d) => [d.field, d]))));
  }, []);

  const columns = useMemo(() => {
    if (!fieldDefs) return null;
    return [
      { field: 'code', header: 'code', displayName: 'Code', type: 'text', width: 200 },
      {
        field: 'prodCode',
        header: 'prod_code',
        displayName: 'Product',
        type: 'entity',
        module: 'md',
        entity: 'PROD',
        required: true,
      },
      {
        field: 'progProdCalcType',
        header: 'prog_prod_calc_type',
        displayName: 'Calc Type',
        type: 'enum',
        values: fieldDefs.progProdCalcType?.values ?? [],
      },
      {
        field: 'priceBasis',
        header: 'price_basis',
        displayName: 'Price Basis',
        type: 'enum',
        values: fieldDefs.pymtBasisId?.values ?? [],
      },
      { field: 'conc', header: 'conc', displayName: 'Concession', type: 'text', width: 120 },
      { field: 'startDate', header: 'start_date', displayName: 'Start Date', type: 'date', required: true, width: 120 },
      { field: 'endDate', header: 'end_date', displayName: 'End Date', type: 'date', width: 120 },
      {
        field: 'endDateAndCreateNew',
        header: 'end_date_and_create_new',
        displayName: 'End-date & new',
        type: 'boolean',
        width: 130,
      },
    ];
  }, [fieldDefs]);

  const existingByCode = useMemo(() => Object.fromEntries(existingRows.map((row) => [row.code, row])), [existingRows]);
  const existingByProdStart = useMemo(
    () => Object.fromEntries(existingRows.map((row) => [`${row.prodCode}|${row.startDate}`, row])),
    [existingRows],
  );

  const classifyRow = (row) => {
    if (row.code && existingByCode[row.code]) return 'UPDATE';
    if (!row.endDateAndCreateNew && existingByProdStart[`${row.prodCode}|${row.startDate}`]) return 'UPDATE';
    return 'NEW';
  };

  const validateRow = (row) => {
    const errors = {};
    if (row.startDate && progObj.startDate && row.startDate < progObj.startDate) {
      errors.startDate = 'Before the Program start date';
    }
    if (row.endDate && progObj.endDate && row.endDate > progObj.endDate) {
      errors.endDate = 'After the Program end date';
    }
    if (row.startDate && row.endDate && row.startDate > row.endDate) {
      errors.endDate = 'Before the start date';
    }
    if (PCT_CALC_TYPES.includes(row.progProdCalcType) && row.conc != null && row.conc !== '') {
      const text = String(row.conc).trim();
      const num = Number(text.endsWith('%') ? text.slice(0, -1) : text);
      if (!Number.isFinite(num)) errors.conc = `Not a number: ${text}`;
    }
    return errors;
  };

  // Percent concessions are stored as decimals (12% -> 0.12); a bare number above 1 is almost always a whole percent
  const warnRow = (row) => {
    if (!PCT_CALC_TYPES.includes(row.progProdCalcType) || row.conc == null || row.conc === '') return {};
    const text = String(row.conc).trim();
    if (text.endsWith('%')) return {};
    const num = Number(text);
    if (Number.isFinite(num) && Math.abs(num) > 1) {
      return { conc: `Read as ${num * 100}%. Enter "${num}%" or ${num / 100} if you meant ${num}%` };
    }
    return {};
  };

  const onSubmit = (rows) =>
    rsrc.store('md', 'PROG_BULK_STORE_PLUGIN', { progId: progObj.id, entity: 'PROG_PROD', rows });

  if (!columns) return null;

  return (
    <EntityBulkGridDialog
      closeDialog={closeDialog}
      title={`Paste Products — ${progObj.code}`}
      subtitle="Rows with a known code (or the same product and start date) update; everything else is added."
      header={<ProgContextStrip progObj={progObj} />}
      columns={columns}
      fixedValues={{}}
      classifyRow={classifyRow}
      validateRow={validateRow}
      warnRow={warnRow}
      onSubmit={onSubmit}
      exportRows={existingRows}
    />
  );
};
